import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import {
  ProductsFilters,
  productsFiltersArray,
  SearchProductsFiltersGeneralQueryParams,
  SearchProductsFiltersResponse,
} from '@interfaces/products.interface';

import {
  useLazySearchProductsFiltersApplicationsQuery,
  useLazySearchProductsFiltersClassificationsQuery,
  useLazySearchProductsFiltersClonalitiesQuery,
  useLazySearchProductsFiltersConjugateQuery,
  useLazySearchProductsFiltersHostSpeciesQuery,
  useLazySearchProductsFiltersIsotypesQuery,
  useLazySearchProductsFiltersReactivitiesQuery,
  useLazySearchProductsFiltersSuppliersQuery,
  useLazySearchProductsFiltersTargetsQuery,
} from '@api/products.api';

const FILTERS_PAGE_LENGTH = 20;

interface ProductsFiltersPaginatedState {
  currentPage: number;
  hasMoreValues: boolean;
  values: { name: string; count: number }[];
}

function getAvailableFiltersForMarket(
  marketCategory: string,
): ProductsFilters[] {
  switch (marketCategory) {
    case 'Cosmetico':
      return ['classifications', 'suppliers'];
    case 'Ricerca':
      return productsFiltersArray;
    default:
      return ['classifications', 'applications'];
  }
}

export default function useProductsFilters(generalQueryParams: {
  marketCategory: string;
  searchQuery?: string;
}) {
  const [searchParams, setSearchParams] = useSearchParams();

  // Filters values
  // --------------------------------------------------------------------------------
  const [availableFilters, setAvailableFilters] = useState<
    Record<ProductsFilters, ProductsFiltersPaginatedState>
  >({
    classifications: {
      currentPage: 1,
      hasMoreValues: true,
      values: [],
    },
    suppliers: {
      currentPage: 1,
      hasMoreValues: true,
      values: [],
    },
    targets: {
      currentPage: 1,
      hasMoreValues: true,
      values: [],
    },
    reactivities: {
      currentPage: 1,
      hasMoreValues: true,
      values: [],
    },
    applications: {
      currentPage: 1,
      hasMoreValues: true,
      values: [],
    },
    hostSpecies: {
      currentPage: 1,
      hasMoreValues: true,
      values: [],
    },
    clonalities: {
      currentPage: 1,
      hasMoreValues: true,
      values: [],
    },
    conjugate: {
      currentPage: 1,
      hasMoreValues: true,
      values: [],
    },
    isotypes: {
      currentPage: 1,
      hasMoreValues: true,
      values: [],
    },
  });

  let filteredAvailableFilters;
  switch (generalQueryParams.marketCategory) {
    case 'Cosmetico':
      filteredAvailableFilters = {
        classifications: availableFilters.classifications,
        suppliers: availableFilters.suppliers,
      };
      break;
    case 'Ricerca':
      filteredAvailableFilters = availableFilters;
      break;
    default:
      filteredAvailableFilters = {
        classifications: availableFilters.classifications,
        applications: availableFilters.applications,
      };
  }

  const [filtersSearchQueries, setFiltersSearchQueries] = useState<
    Record<ProductsFilters, string>
  >({
    targets: '',
    reactivities: '',
    hostSpecies: '',
    conjugate: '',
    clonalities: '',
    isotypes: '',
    classifications: '',
    applications: '',
    suppliers: '',
  });

  // Filters api
  // --------------------------------------------------------------------------------
  const [getTargetsFilters, { isFetching: isFetchingTargets }] =
    useLazySearchProductsFiltersTargetsQuery();
  const [getReactivitiesFilters, { isFetching: isFetchingReactivities }] =
    useLazySearchProductsFiltersReactivitiesQuery();
  const [getHostSpeciesFilters, { isFetching: isFetchingHostSpecies }] =
    useLazySearchProductsFiltersHostSpeciesQuery();
  const [getConjugateFilters, { isFetching: isFetchingConjugate }] =
    useLazySearchProductsFiltersConjugateQuery();
  const [getClonalitiesFilters, { isFetching: isFetchingClonalities }] =
    useLazySearchProductsFiltersClonalitiesQuery();
  const [getIsotypesFilters, { isFetching: isFetchingIsotypes }] =
    useLazySearchProductsFiltersIsotypesQuery();
  const [getClassificationsFilters, { isFetching: isFetchingClassifications }] =
    useLazySearchProductsFiltersClassificationsQuery();
  const [getApplicationsFilters, { isFetching: isFetchingApplications }] =
    useLazySearchProductsFiltersApplicationsQuery();
  const [getSuppliersFilters, { isFetching: isFetchingSuppliers }] =
    useLazySearchProductsFiltersSuppliersQuery();

  const fetchFiltersValues = useCallback(
    async (
      filters: ProductsFilters[],
      currentAvailableFilters: Record<
        ProductsFilters,
        ProductsFiltersPaginatedState
      >, // Passed as parameter to avoid async set state issues
      queryParams: SearchProductsFiltersGeneralQueryParams, // Passed as parameter to avoid async set state issues
      filtersSearchQueries: Record<ProductsFilters, string>, // Passed as parameter to avoid async set state issues
    ) => {
      const fetchedFilters = currentAvailableFilters;

      // const apiPromises: Promise<SearchProductsFiltersResponse>[] = [];

      function setFilters(
        filter: ProductsFilters,
        res: SearchProductsFiltersResponse[],
      ) {
        fetchedFilters[filter] = {
          currentPage: fetchedFilters[filter].currentPage,
          hasMoreValues: res.length >= FILTERS_PAGE_LENGTH,
          values: [...fetchedFilters[filter].values, ...res],
        };

        setAvailableFilters(fetchedFilters);
        setFiltersSearchQueries({
          ...filtersSearchQueries,
          [filter]: filtersSearchQueries[filter],
        });
      }

      for (const filter of filters) {
        switch (filter) {
          case 'targets':
            {
              // eslint-disable-next-line @typescript-eslint/no-unused-vars
              const { targets, ...rest } = queryParams;
              getTargetsFilters({
                ...rest,
                filterSearchQuery: filtersSearchQueries[filter],
                page: fetchedFilters[filter].currentPage,
                pageLength: FILTERS_PAGE_LENGTH,
              })
                .unwrap()
                .then((res) => setFilters(filter, res));
            }
            break;
          case 'reactivities':
            {
              // eslint-disable-next-line @typescript-eslint/no-unused-vars
              const { reactivities, ...rest } = queryParams;
              getReactivitiesFilters({
                ...rest,
                filterSearchQuery: filtersSearchQueries[filter],
                page: fetchedFilters[filter].currentPage,
                pageLength: FILTERS_PAGE_LENGTH,
              })
                .unwrap()
                .then((res) => setFilters(filter, res));
            }
            break;
          case 'hostSpecies':
            {
              // eslint-disable-next-line @typescript-eslint/no-unused-vars
              const { hostSpecies, ...rest } = queryParams;
              getHostSpeciesFilters({
                ...rest,
                filterSearchQuery: filtersSearchQueries[filter],
                page: fetchedFilters[filter].currentPage,
                pageLength: FILTERS_PAGE_LENGTH,
              })
                .unwrap()
                .then((res) => setFilters(filter, res));
            }
            break;
          case 'conjugate':
            {
              // eslint-disable-next-line @typescript-eslint/no-unused-vars
              const { conjugate, ...rest } = queryParams;
              getConjugateFilters({
                ...rest,
                filterSearchQuery: filtersSearchQueries[filter],
                page: fetchedFilters[filter].currentPage,
                pageLength: FILTERS_PAGE_LENGTH,
              })
                .unwrap()
                .then((res) => setFilters(filter, res));
            }
            break;
          case 'clonalities':
            {
              // eslint-disable-next-line @typescript-eslint/no-unused-vars
              const { clonalities, ...rest } = queryParams;
              getClonalitiesFilters({
                ...rest,
                filterSearchQuery: filtersSearchQueries[filter],
                page: fetchedFilters[filter].currentPage,
                pageLength: FILTERS_PAGE_LENGTH,
              })
                .unwrap()
                .then((res) => setFilters(filter, res));
            }
            break;
          case 'isotypes':
            {
              // eslint-disable-next-line @typescript-eslint/no-unused-vars
              const { isotypes, ...rest } = queryParams;
              getIsotypesFilters({
                ...rest,
                filterSearchQuery: filtersSearchQueries[filter],
                page: fetchedFilters[filter].currentPage,
                pageLength: FILTERS_PAGE_LENGTH,
              })
                .unwrap()
                .then((res) => setFilters(filter, res));
            }
            break;
          case 'classifications':
            {
              // eslint-disable-next-line @typescript-eslint/no-unused-vars
              const { classifications, ...rest } = queryParams;
              getClassificationsFilters({
                ...rest,
                filterSearchQuery: filtersSearchQueries[filter],
                page: fetchedFilters[filter].currentPage,
                pageLength: FILTERS_PAGE_LENGTH,
              })
                .unwrap()
                .then((res) => setFilters(filter, res));
            }
            break;
          case 'applications':
            {
              // eslint-disable-next-line @typescript-eslint/no-unused-vars
              const { applications, ...rest } = queryParams;
              getApplicationsFilters({
                ...rest,
                filterSearchQuery: filtersSearchQueries[filter],
                page: fetchedFilters[filter].currentPage,
                pageLength: FILTERS_PAGE_LENGTH,
              })
                .unwrap()
                .then((res) => setFilters(filter, res));
            }
            break;
          case 'suppliers':
            {
              // eslint-disable-next-line @typescript-eslint/no-unused-vars
              const { suppliers, ...rest } = queryParams;
              getSuppliersFilters({
                ...rest,
                filterSearchQuery: filtersSearchQueries[filter],
                page: fetchedFilters[filter].currentPage,
                pageLength: FILTERS_PAGE_LENGTH,
              })
                .unwrap()
                .then((res) => setFilters(filter, res));
            }
            break;
        }
      }

      // const results = await Promise.all(apiPromises);

      // // Results length is equal to filters length
      // for (let i = 0; i < filters.length; i++) {
      //   fetchedFilters[filters[i]] = {
      //     currentPage: currentAvailableFilters[filters[i]].currentPage,
      //     hasMoreValues: results[i].values.length >= FILTERS_PAGE_LENGTH,
      //     totalCount: results[i].totalCount,
      //     values: [
      //       ...currentAvailableFilters[filters[i]].values,
      //       ...results[i].values,
      //     ],
      //   };
      // }

      // setAvailableFilters(fetchedFilters);
    },
    [
      getTargetsFilters,
      getReactivitiesFilters,
      getHostSpeciesFilters,
      getConjugateFilters,
      getClonalitiesFilters,
      getIsotypesFilters,
      getClassificationsFilters,
      getApplicationsFilters,
      getSuppliersFilters,
    ],
  );

  // Selected filters
  // --------------------------------------------------------------------------------
  const [selectedFilters, setSelectedFilters] = useState<
    Record<ProductsFilters, string[]>
  >({
    targets: searchParams.getAll('targets') || [],
    reactivities: searchParams.getAll('reactivities') || [],
    hostSpecies: searchParams.getAll('hostSpecies') || [],
    conjugate: searchParams.getAll('conjugate') || [],
    clonalities: searchParams.getAll('clonalities') || [],
    isotypes: searchParams.getAll('isotypes') || [],
    classifications: searchParams.getAll('classifications') || [],
    applications: searchParams.getAll('applications') || [],
    suppliers: searchParams.getAll('suppliers') || [],
  });

  // Change selected filters function
  // --------------------------------------------------------------------------------
  const changeSelectedFilters = useCallback(
    (filter: ProductsFilters, selectedValues: string[]) => {
      const newSelectedFilters = {
        ...selectedFilters,
        [filter]: selectedValues,
      };

      setSelectedFilters(newSelectedFilters);

      // Reset pagination of other filters
      const newAvailableFilters = Object.entries(availableFilters).reduce(
        (acc, [key, value]) => {
          if (key !== filter) {
            // Reset values and pagination of other filters
            acc[key as ProductsFilters] = {
              currentPage: 1,
              hasMoreValues: true,
              values: [],
            };
          } else {
            // Keep values and pagination of the selected filter
            acc[key as ProductsFilters] = value;
          }

          return acc;
        },
        {} as Record<ProductsFilters, ProductsFiltersPaginatedState>,
      );

      // Refetch other filters (only those available for the current market)
      const availableFiltersForMarket = getAvailableFiltersForMarket(
        generalQueryParams.marketCategory,
      );
      const filtersToRefetch = availableFiltersForMarket.filter(
        (key) => key !== filter,
      );

      fetchFiltersValues(
        filtersToRefetch,
        newAvailableFilters,
        {
          ...generalQueryParams,
          ...newSelectedFilters,
        },
        filtersSearchQueries,
      );

      setSearchParams((prev) => {
        prev.delete(filter);
        selectedValues.forEach((value) => {
          prev.append(filter, value);
        });

        prev.set('page', '1'); // TODO: improve this. it makes two calls

        return prev;
      });
    },
    [
      availableFilters,
      generalQueryParams,
      selectedFilters,
      filtersSearchQueries,
    ],
  );

  const clearAllFilters = useCallback(
    (newSearchQuery?: string) => {
      const newSelectedFilters: Record<ProductsFilters, string[]> = {
        targets: [],
        reactivities: [],
        hostSpecies: [],
        conjugate: [],
        clonalities: [],
        isotypes: [],
        classifications: [],
        applications: [],
        suppliers: [],
      };

      setSelectedFilters(newSelectedFilters);

      // Reset pagination of all filters
      const newAvailableFilters = Object.keys(availableFilters).reduce(
        (acc, key) => {
          acc[key as ProductsFilters] = {
            currentPage: 1,
            hasMoreValues: true,
            values: [],
          };
          return acc;
        },
        {} as Record<ProductsFilters, ProductsFiltersPaginatedState>,
      );

      const queryParams = {
        ...generalQueryParams,
        searchQuery:
          newSearchQuery !== undefined
            ? newSearchQuery
            : generalQueryParams.searchQuery,
        ...newSelectedFilters,
      };

      // Refetch all filters
      const filtersToFetch = getAvailableFiltersForMarket(
        generalQueryParams.marketCategory,
      );

      fetchFiltersValues(
        filtersToFetch,
        newAvailableFilters,
        queryParams, // Usa i parametri preparati
        filtersSearchQueries,
      );

      setSearchParams((prev) => {
        // Remove all filters
        for (const filter of productsFiltersArray) {
          prev.delete(filter);
        }

        // Set new searchQuery (new or existing)
        if (newSearchQuery !== undefined) {
          prev.set('searchQuery', newSearchQuery);
        } else if (generalQueryParams.searchQuery) {
          prev.set('searchQuery', generalQueryParams.searchQuery);
        }

        prev.set('page', '1');
        return prev;
      });
    },
    [
      availableFilters,
      generalQueryParams,
      filtersSearchQueries,
      fetchFiltersValues,
      setSearchParams,
    ],
  );

  // Load more filter values function
  // --------------------------------------------------------------------------------
  const setFilterSearchQuery = useCallback(
    (filter: ProductsFilters, filterSearchQuery: string) => {
      const newFiltersSearchQueries = {
        ...filtersSearchQueries,
        [filter]: filterSearchQuery,
      };
      const newAvailableFilters = {
        ...availableFilters,
        [filter]: {
          currentPage: 1,
          hasMoreValues: true,
          values: [],
        },
      };

      fetchFiltersValues(
        [filter],
        newAvailableFilters,
        {
          ...generalQueryParams,
          ...selectedFilters,
        },
        newFiltersSearchQueries,
      );
    },
    [
      availableFilters,
      generalQueryParams,
      selectedFilters,
      filtersSearchQueries,
    ],
  );

  const loadMoreFilterValues = useCallback(
    (filter: ProductsFilters) => {
      const newAvailableFilters = {
        ...availableFilters,
        [filter]: {
          ...availableFilters[filter],
          currentPage: availableFilters[filter].currentPage + 1,
        },
      };

      fetchFiltersValues(
        [filter],
        newAvailableFilters,
        {
          ...generalQueryParams,
          ...selectedFilters,
        },
        filtersSearchQueries,
      );
    },
    [availableFilters, generalQueryParams, selectedFilters],
  );

  // Reset all available filters on search query change
  // --------------------------------------------------------------------------------
  useEffect(() => {
    const newAvailableFilters = {
      classifications: {
        currentPage: 1,
        hasMoreValues: true,
        totalCount: 0,
        values: [],
      },
      suppliers: {
        currentPage: 1,
        hasMoreValues: true,
        totalCount: 0,
        values: [],
      },
      targets: {
        currentPage: 1,
        hasMoreValues: true,
        values: [],
      },
      reactivities: {
        currentPage: 1,
        hasMoreValues: true,
        values: [],
      },
      applications: {
        currentPage: 1,
        hasMoreValues: true,
        totalCount: 0,
        values: [],
      },
      hostSpecies: {
        currentPage: 1,
        hasMoreValues: true,
        totalCount: 0,
        values: [],
      },
      clonalities: {
        currentPage: 1,
        hasMoreValues: true,
        totalCount: 0,
        values: [],
      },
      conjugate: {
        currentPage: 1,
        hasMoreValues: true,
        totalCount: 0,
        values: [],
      },
      isotypes: {
        currentPage: 1,
        hasMoreValues: true,
        totalCount: 0,
        values: [],
      },
    };

    // Refetch all filters
    const filtersToFetch = getAvailableFiltersForMarket(
      generalQueryParams.marketCategory,
    );
    fetchFiltersValues(
      filtersToFetch,
      newAvailableFilters,
      {
        ...generalQueryParams,
        ...selectedFilters,
      },
      filtersSearchQueries,
    );
  }, [generalQueryParams.searchQuery]);

  const filtersFetchingState = useMemo(() => {
    return {
      targets: isFetchingTargets,
      reactivities: isFetchingReactivities,
      hostSpecies: isFetchingHostSpecies,
      conjugate: isFetchingConjugate,
      clonalities: isFetchingClonalities,
      isotypes: isFetchingIsotypes,
      classifications: isFetchingClassifications,
      applications: isFetchingApplications,
      suppliers: isFetchingSuppliers,
    };
  }, [
    isFetchingTargets,
    isFetchingReactivities,
    isFetchingHostSpecies,
    isFetchingConjugate,
    isFetchingClonalities,
    isFetchingIsotypes,
    isFetchingClassifications,
    isFetchingApplications,
    isFetchingSuppliers,
  ]);

  return {
    availableFilters: filteredAvailableFilters,
    selectedFilters,
    filtersFetchingState,
    clearAllFilters,
    changeSelectedFilters,
    loadMoreFilterValues,
    setFilterSearchQuery,
  };
}
